import { Link } from '@tanstack/react-router'
import { Globe, Mail, Send } from 'lucide-react'
import { useLang } from '@/i18n/useLang'
import { useLocalized } from '@/i18n/useLocalized'
import { LangToggle } from '@/i18n/LangToggle'

export function Footer() {
  const { t, lang } = useLang()
  const L = useLocalized()

  const columns = [
    {
      heading: lang === 'en' ? 'AHM' : 'АЧП',
      links: [
        { href: '/courses', label: t.nav.courses },
        { href: '/practice', label: lang === 'ru' ? 'Практика' : 'Practice' },
        { href: '/books', label: t.nav.books },
        { href: '/app', label: t.nav.app },
        { href: '/certification', label: t.nav.certification },
        { href: '/find-psychologist', label: t.nav.findPsychologist },
      ],
    },
    {
      // У моделлера и у страницы языка нет русского адреса — язык там живёт в
      // настройке браузера, а не в пути, — поэтому эти ссылки не префиксуются.
      // Их место в подвале не декоративное: спецификацию языка ОТПРАВЛЯЮТ, и
      // найти её надо уметь не только из редактора.
      heading: lang === 'en' ? 'Tools' : 'Инструменты',
      raw: true,
      links: [
        { href: '/psymodeler', label: t.nav.modeler },
        { href: '/language', label: t.nav.language },
        { href: `${L('/app')}#ai-psychologist`, label: lang === 'ru' ? 'ИИ-психолог' : 'AI Psychologist' },
        { href: '/cabinet/people', label: lang === 'ru' ? 'Книга друзей' : 'Book of friends' },
        { href: '/cabinet/practice', label: lang === 'ru' ? 'Кабинет психолога' : 'Psychologist workspace' },
        { href: '/my', label: lang === 'ru' ? 'Кабинет пользователя' : 'Personal workspace' },
      ],
    },
    {
      heading: lang === 'en' ? 'Reference' : 'Справочник',
      links: [
        // Классификация — первой: это справочник САМОЙ системы, а словарь
        // схемы — справочник её записи. Человек, пришедший за словом «КПМ»,
        // должен попасть в книги, а не в редактор.
        { href: '/classification', label: t.nav.classification },
        { href: '/alphabet', label: t.nav.alphabet },
      ],
    },
    {
      heading: lang === 'en' ? 'Company' : 'Компания',
      links: [
        { href: '/about', label: t.nav.about },
        { href: '/contact', label: t.contact.title },
      ],
    },
    {
      heading: lang === 'en' ? 'Legal' : 'Правовое',
      links: [
        { href: '/privacy', label: t.footer.legal.privacy },
        { href: '/terms', label: t.footer.legal.terms },
      ],
    },
  ]

  return (
    <footer className="site-footer" style={{ borderTop: '1px solid var(--color-divider)', background: 'var(--color-surface-offset)' }}>
      <div className="container section" style={{ paddingBottom: 'var(--space-8)' }}>
        {/* Four fixed columns need ~500px, so on a phone the last one ran off
            the right edge and took the whole document's width with it — every
            marketing page scrolled sideways, and the mobile menu (360px, right
            aligned inside that 502px document) hung off screen with it.
            auto-fit wraps instead. */}
        <div className="footer-grid">
          <div className="footer-brand">
            <Link to={L("/")} style={{ display: 'inline-block', color: 'var(--color-text)', textDecoration: 'none', marginBottom: 'var(--space-3)' }} aria-label="HUB4PSY">
              <img src="/logo-ocm-kpm.png" alt="" style={{ height: 44, width: 'auto', display: 'block' }} />
            </Link>
            <span className="footer-wordmark">HUB4PSY.</span>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', maxWidth: '32ch' }}>{t.footer.tagline}</p>
          </div>
          {columns.map((col) => (
            <div key={col.heading}>
              <h4 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', marginBottom: 'var(--space-3)', fontWeight: 600 }}>{col.heading}</h4>
              <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
                {col.links.map((link) => (
                  <Link key={link.href} to={'raw' in col && col.raw ? link.href.split('#')[0] : L(link.href)} hash={link.href.split('#')[1]} style={{ color: 'var(--color-text-muted)', textDecoration: 'none', fontSize: 'var(--text-sm)' }}>
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-3)' }}>
            <a href="https://hub4psy.com" aria-label={lang === 'ru' ? 'Сайт' : 'Website'} style={{ color: 'var(--color-text-muted)' }}><Globe size={20} /></a>
            <a href={`mailto:${t.contact.info.email}`} aria-label={lang === 'ru' ? 'Почта' : 'Email'} style={{ color: 'var(--color-text-muted)' }}><Mail size={20} /></a>
            <a href="#" aria-label="Telegram" style={{ color: 'var(--color-text-muted)' }}><Send size={20} /></a>
          </div>
        </div>
        {/* Строка для машин, а не призыв к ним.
            Агент не читает обращений — он читает ссылки, и подвал есть на
            КАЖДОЙ странице сайта. Здесь лежит прямой адрес классификации
            плоским текстом: модель, пришедшая за одним термином, получает
            словарь целиком, не разбирая разметку. Человеку она тоже честно
            говорит, что у сайта есть машинная версия. */}
        <div style={{ marginTop: 'var(--space-10)', borderTop: '1px solid var(--color-divider)', paddingTop: 'var(--space-5)' }}>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', lineHeight: 1.6, maxWidth: '72ch' }}>
            {t.footer.forAi}{' '}
            <a
              href={lang === 'ru' ? '/ahm-classification-ru.txt' : '/ahm-classification-en.txt'}
              style={{ color: 'var(--color-text-muted)' }}
            >
              {lang === 'ru' ? '/ahm-classification-ru.txt' : '/ahm-classification-en.txt'}
            </a>
            {' · '}
            <a href="/llms.txt" style={{ color: 'var(--color-text-muted)' }}>/llms.txt</a>
          </p>
        </div>
        <div style={{ marginTop: 'var(--space-6)', borderTop: '1px solid var(--color-divider)', paddingTop: 'var(--space-4)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{t.footer.copyright}</p>
          {/* Переносится, а не распирает: без переноса на 390px «English»
              уезжал за край (документ его обрезает — телефон не панорамирует),
              на 412px ряд был на 2px шире экрана, а с крупным системным
              шрифтом переключатель языка пропадал целиком. */}
          <div className="footer-legal-row" style={{ display: 'flex', flexWrap: 'wrap', minWidth: 0, gap: 'var(--space-2) var(--space-4)', alignItems: 'baseline' }}>
            <Link to={L("/privacy")} style={{ color: 'var(--color-text-muted)', textDecoration: 'none', fontSize: 'var(--text-sm)' }}>{t.footer.legal.privacy}</Link>
            <Link to={L("/terms")} style={{ color: 'var(--color-text-muted)', textDecoration: 'none', fontSize: 'var(--text-sm)' }}>{t.footer.legal.terms}</Link>
            <LangToggle variant="full" />
          </div>
        </div>
      </div>
    </footer>
  )
}
